import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { SunHorizonIcon, SpinnerGapIcon, TrophyIcon, RocketLaunchIcon, BinocularsIcon, ArrowRightIcon } from "@phosphor-icons/react";
import { fetchMorningCall } from "../lib/api";
import { fmtUsd } from "../lib/format";
import { useSeo } from "../lib/useSeo";

const GOLD = "#F0C000";
const short = (a) => (a && a.length > 8 ? `${a.slice(0, 4)}…${a.slice(-4)}` : a || "");
const ago = (ts) => {
  if (!ts) return "";
  const h = Math.floor((Date.now() / 1000 - ts) / 3600);
  return h < 1 ? "just now" : `${h}h ago`;
};

const Stat = ({ label, value, color }) => (
  <div className="border border-white/10 bg-black/20 py-3 text-center flex-1">
    <div className="font-heading font-black text-2xl" style={color ? { color } : {}}>{value}</div>
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">{label}</div>
  </div>
);

const Line = ({ mint, symbol, tag, sub, tagColor, testid }) => (
  <Link to={`/coin/${mint}`} data-testid={testid} className="flex items-center gap-3 px-4 py-3 border border-white/5 hover:bg-white/[0.03] hover:border-neon/30 transition-colors">
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-heading font-bold">${symbol || short(mint)}</span>
        {tag && <span className="font-mono text-[9px] px-1.5 py-0.5 font-bold" style={{ background: `${tagColor}18`, border: `1px solid ${tagColor}`, color: tagColor }}>{tag}</span>}
      </div>
      {sub && <div className="font-mono text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
    <ArrowRightIcon size={14} className="text-muted-foreground shrink-0" />
  </Link>
);

export default function MorningCall() {
  useSeo({
    title: "☕ The Morning Call — What Mattered While You Slept | AlphaApeing",
    description: "A once-a-day brief that ranks the night's best setups across every radar — breakouts, highest-conviction, survivor pops — plus the one to look at.",
  });

  const { data, isLoading } = useQuery({ queryKey: ["morningCall"], queryFn: fetchMorningCall, refetchInterval: 120000 });
  const st = data?.stats || {};
  const top = data?.topPick;
  const conv = data?.convictions || [];
  const brk = data?.breakouts || [];
  const pops = data?.survivorPops || [];
  const convMints = new Set(conv.map((c) => c.mint));

  return (
    <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-6 space-y-6 animate-fade-up" data-testid="morning-call-page">
      <div>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 flex items-center justify-center text-black" style={{ background: GOLD }}><SunHorizonIcon size={24} weight="bold" /></div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none">THE MORNING <span style={{ color: GOLD }}>CALL</span></h1>
        </div>
        <p className="text-muted-foreground mt-3 text-sm leading-relaxed max-w-2xl">
          What mattered <span className="text-white font-semibold">while you slept</span> — every radar's best setups from the last 24h, ranked into one brief with the
          <span style={{ color: GOLD }} className="font-semibold"> one to look at</span>. Get it as a daily Telegram at ~9am ET (toggle "Morning Call" on the <Link to="/alerts" className="text-neon hover:underline">Alerts</Link> page).
        </p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16"><SpinnerGapIcon size={30} className="animate-spin" style={{ color: GOLD }} /></div>
      ) : (
        <>
          <div className="flex gap-3" data-testid="morning-call-stats">
            <Stat label="scanned" value={st.scanned ?? 0} />
            <Stat label="breakouts" value={st.breakouts ?? 0} color="#FF7A00" />
            <Stat label="conviction" value={st.convictions ?? 0} color={GOLD} />
            <Stat label="survivor pops" value={st.survivorPops ?? 0} color="#00E599" />
          </div>

          {top && top.symbol && (
            <div className="border p-5" style={{ borderColor: `${GOLD}55`, background: `linear-gradient(90deg, ${GOLD}12, transparent)` }} data-testid="morning-call-toppick">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">👉 The one to look at</div>
              <Link to={`/coin/${top.mint}`} className="flex items-center gap-3 group">
                <div className="w-10 h-10 flex items-center justify-center text-black shrink-0" style={{ background: GOLD }}>
                  {top.kind === "conviction" ? <TrophyIcon size={20} weight="fill" /> : <RocketLaunchIcon size={20} weight="fill" />}
                </div>
                <div className="min-w-0">
                  <div className="font-heading font-black text-2xl group-hover:text-neon transition-colors">${top.symbol}{top.handle ? <span className="text-muted-foreground text-base font-mono"> @{top.handle}</span> : null}</div>
                  <div className="font-mono text-xs text-muted-foreground">
                    {top.kind === "conviction" ? "🏆 Highest-conviction" : "🚀 Top breakout"}
                    {top.score ? ` · score ${top.score}` : ""}{top.surge ? ` · vol ${top.surge}×` : ""}{top.mcap ? ` · ${fmtUsd(top.mcap)}` : ""}
                  </div>
                </div>
              </Link>
            </div>
          )}

          {conv.length > 0 && (
            <div className="space-y-2" data-testid="morning-call-conviction">
              <h2 className="font-heading font-black text-lg tracking-tight flex items-center gap-2"><TrophyIcon size={18} weight="fill" style={{ color: GOLD }} /> Highest-conviction</h2>
              {conv.map((c) => <Line key={c.mint} mint={c.mint} symbol={c.symbol} tag="🏆 HANDLE + BREAKOUT" tagColor={GOLD} sub={`${c.handle ? "@" + c.handle + " · " : ""}vol ${c.surge}× · score ${c.score ?? "—"} · ${fmtUsd(c.mcap)}`} testid={`mc-conv-${c.mint}`} />)}
            </div>
          )}

          {brk.filter((b) => !convMints.has(b.mint)).length > 0 && (
            <div className="space-y-2" data-testid="morning-call-breakouts">
              <h2 className="font-heading font-black text-lg tracking-tight flex items-center gap-2"><RocketLaunchIcon size={18} weight="fill" style={{ color: "#FF7A00" }} /> Breakouts</h2>
              {brk.filter((b) => !convMints.has(b.mint)).map((b) => <Line key={b.mint} mint={b.mint} symbol={b.symbol} tag={b.reclaim ? "♻️ RECLAIM" : "🚀 BREAKOUT"} tagColor="#FF7A00" sub={`vol ${b.surge}× · 6h ${b.h6 != null ? Math.round(b.h6) + "%" : "—"} · ${fmtUsd(b.mcap)}`} testid={`mc-brk-${b.mint}`} />)}
            </div>
          )}

          {pops.length > 0 && (
            <div className="space-y-2" data-testid="morning-call-pops">
              <h2 className="font-heading font-black text-lg tracking-tight flex items-center gap-2"><BinocularsIcon size={18} weight="fill" style={{ color: "#00E599" }} /> Survivor Watch pops</h2>
              {pops.map((p) => <Line key={p.mint} mint={p.mint} symbol={p.symbol} tag="🚀 POPPED" tagColor="#00E599" sub={`${fmtUsd(p.mcap)} · popped ${ago(p.poppedTs)}`} testid={`mc-pop-${p.mint}`} />)}
            </div>
          )}

          {conv.length === 0 && brk.length === 0 && pops.length === 0 && (
            <div className="border border-white/10 bg-card py-14 text-center text-muted-foreground font-mono text-sm px-6">
              Quiet night — no qualified setups fired in the last 24h. No forced trades is a good night.
            </div>
          )}
        </>
      )}
    </div>
  );
}
